import type { FeatureCollection, Geometry } from 'geojson';

/* eslint-disable @typescript-eslint/no-restricted-imports */
import type {
  CadenzaEvent,
  Feature as CadenzaFeature,
  Extent,
  ExtentStrategy,
  FilterVariables,
  CadenzaEventByType as PublicCadenzaEventByType,
  CadenzaEventType as PublicCadenzaEventType,
  WorkbookLayerPath,
} from '@disy/cadenza.js';
import { assertNonNullable } from '@disy/lib/custom-error';
import { getDisyNamespace } from '@disy/lib/disy';
import { isTest } from '@disy/lib/env';
import { getLogger } from '@disy/lib/logging';

import { isFeatureAvailable } from 'cadenza/features';
import type { SelectionEventDetail } from 'cadenza/integration/cadenza-api-selection-util';
import type { CustomValidation } from 'cadenza/integration/custom-validation';

const logger = getLogger('cadenza/integration/post-message');

if (isFeatureAvailable('CADENZA_JS_SANDBOX')) {
  logger.enableAll();
}

type OutgoingCadenzaEventType = PublicCadenzaEventType | 'ready' | 'change:extent';
type OutgoingCadenzaEvent<T extends OutgoingCadenzaEventType> =
  T extends PublicCadenzaEventType ? PublicCadenzaEventByType<T>
  : T extends 'change:extent' ? CadenzaEvent<T, { extent: Extent }>
  : never;

type IncomingCadenzaEventType =
  | 'expandNavigator'
  | 'getData'
  | 'setCustomValidity'
  | 'setGeometry'
  | 'setExtentStrategy'
  | 'setFilter'
  | 'setLayerVisibility'
  | 'setSelection'
  | 'addSelection'
  | 'removeSelection'
  | 'reload'
  | 'closeMe'
  | 'importLayer'
  | 'createFeatures'
  | 'requestEditFeature'
  | 'setEditorState';

export type IncomingCadenzaEvent<T extends IncomingCadenzaEventType = IncomingCadenzaEventType> = {
  responsePort?: MessagePort;
} & (T extends 'expandNavigator' ? CadenzaEvent<T, { expandNavigator: boolean }>
: T extends 'getData' ? CadenzaEvent<T, { width?: number; height?: number; withScale?: boolean }>
: T extends 'setCustomValidity' ? CadenzaEvent<T, CustomValidation>
: T extends 'setGeometry' ? CadenzaEvent<T, { geometry: Geometry; zoomToGeometry?: boolean }>
: T extends 'setFilter' ? CadenzaEvent<T, { filter: FilterVariables }>
: T extends 'setLayerVisibility' ? CadenzaEvent<T, { layer: WorkbookLayerPath; visible: boolean }>
: T extends 'setSelection' ? CadenzaEvent<T, SelectionEventDetail>
: T extends 'addSelection' ? CadenzaEvent<T, SelectionEventDetail>
: T extends 'removeSelection' ? CadenzaEvent<T, SelectionEventDetail>
: T extends 'reload' ? CadenzaEvent<T, { invalidateCaches: boolean }>
: T extends 'importLayer' ?
  CadenzaEvent<
    T,
    {
      content: FeatureCollection;
      name: string;
      type: 'geojson';
    }
  >
: T extends 'setExtentStrategy' ? CadenzaEvent<T, ExtentStrategy>
: T extends 'createFeatures' ? CadenzaEvent<T, FeatureCollection>
: T extends 'requestEditFeature' ? CadenzaEvent<T, CadenzaFeature['objectId']>
: T extends 'setEditorState' ? CadenzaEvent<T, 'READY' | 'LOADING'>
: never);

type Subscriber<T extends IncomingCadenzaEventType = never> = (event: IncomingCadenzaEvent<T>) => void;

interface Subscription {
  type: IncomingCadenzaEventType;
  sourceWindow?: Window;
  subscriber: Subscriber<IncomingCadenzaEventType>;
}

const subscriptions: Subscription[] = [];

/**
 * Subscribe to events from the specified window.
 *
 * @param type - The event type
 * @param subscriber - The subscriber function
 * @param [sourceWindow] - The expected source window of the events; If unspecified, events from any window are accepted.
 * @return An unsubscribe function
 */
export function subscribeToEvent<T extends IncomingCadenzaEventType>(
  type: T,
  subscriber: Subscriber<T>,
  sourceWindow?: Window,
): () => void {
  if (subscriptions.length === 0) {
    window.addEventListener('message', __onMessage__);
  }
  subscriptions.push({ type, sourceWindow, subscriber: subscriber as Subscriber<IncomingCadenzaEventType> });
  return () => {
    subscriptions.forEach(
      ({ type: subscriptionType, sourceWindow: subscriptionSourceWindow, subscriber: subscriptionSubscriber }, i) => {
        if (
          subscriptionType === type &&
          subscriptionSourceWindow === sourceWindow &&
          subscriptionSubscriber === subscriber
        ) {
          subscriptions.splice(i, 1);
        }
      },
    );
    if (subscriptions.length === 0) {
      window.removeEventListener('message', __onMessage__);
    }
  };
}

// Exported for testing
export function __onMessage__(event: MessageEvent<IncomingCadenzaEvent>) {
  if (
    getDisyNamespace().targetOrigin !== '*' &&
    location.origin !== event.origin &&
    !isKnownSourceOrigin(event.origin)
  ) {
    return;
  }

  logger.log('Received message', event);

  const cadenzaEvent = event.data;
  cadenzaEvent.responsePort = event.ports[0];
  subscriptions.forEach(({ type, sourceWindow, subscriber }) => {
    if (type === cadenzaEvent.type && (!sourceWindow || sourceWindow === event.source)) {
      subscriber(cadenzaEvent);
    }
  });
}

/** Check whether the origin of the given URL is a known source for post messages. */
export function isKnownSourceOrigin(url: string): boolean {
  // try-catch-block can be replaced with URL.canParse when browser/JsDOM support is better
  try {
    return getDisyNamespace().knownSourceOrigins?.includes(new URL(url).origin) ?? false;
  } catch {
    logger.warn(`Invalid url: ${url}`);
    return false;
  }
}

/**
 * Post an event to the target window using `postMessage()`.
 *
 * - The default options are set up for cases when Cadenza is embedded in a custom app.
 * - The `Disy.targetOrigin` can be changed via
 *   - [The `webApplication` option of Cadenza JS](https://disyinformationssysteme.github.io/cadenza.js/interfaces/CadenzaClientOptions.html#webApplication)
 *   - The `net.disy.cadenza.postMessageAnyOrigin` feature toggle
 *   - (@see net.disy.cadenza.web.pebble.TemplateContextProvider#getTargetOrigin)
 *
 * @param type - The event type
 * @param [detail] - The event detail
 * @param [options]
 * @param [options.targetWindow] - The window to post the event to, by default the `window.parent`
 * @param [options.targetOrigin] - The target origin for the message, by default the `Disy.targetOrigin` or the `location.origin`
 */
export function postEvent<T extends OutgoingCadenzaEventType>(
  type: T,
  detail?: OutgoingCadenzaEvent<T>['detail'],
  options: { targetWindow?: Window; targetOrigin?: string } = {},
) {
  const { targetWindow = window.parent, targetOrigin = getDisyNamespace().targetOrigin ?? location.origin } = options;
  const event = { type, detail };
  logger.log(`postEvent() of type '${type}' to ${targetOrigin} and window `, window, event);
  if (type === 'ready' && !isTest()) {
    /* eslint-disable no-console -- Use console instead of logger, so that e.g. app developers don't need to set log level */
    console.log(
      `A custom application needs to listen for the "ready" event on ${location.origin} from window `,
      window,
    );
  }
  targetWindow.postMessage(event, targetOrigin);
}

type RequestResponse<T extends IncomingCadenzaEventType> = T extends 'getData' ? Blob : never;

/**
 * A convenient utility for use with {@link subscribeToEvent} to answer requests from a custom app
 *
 * @param event - The request event
 * @param promise - The result promise:
 * - When resolved, the request is answered with a `${event.type}:success` event
 *   with the promise value as the event detail.
 * - When rejected, the request is answered with a `${event.type}:error` event without event detail.
 * @example
 *   subscribeToEvent('setSelection', (event) =>
 *     // Posts either a 'setSelection:success' or a 'setSelection:error' event.
 *     handleRequest(event, setSelectionAsync(event.detail)),
 *   );
 */
export async function handleRequest<T extends IncomingCadenzaEventType>(
  event: IncomingCadenzaEvent<T>,
  promise: Promise<void | RequestResponse<T>>,
) {
  const port = event.responsePort;
  assertNonNullable(port, 'The response port is required to handle a request');
  try {
    port.postMessage({
      type: `${event.type}:success`,
      detail: await promise,
    });
  } catch {
    port.postMessage({ type: `${event.type}:error` });
  }
}
